/**
 * Export CSV des pages de référence (contenu complet, exploitable dans une IA).
 * Format Excel FR : séparateur point-virgule, BOM UTF-8.
 */

import { getAuditByUrl } from '../api';
import { normalizePageUrl, resolveReferenceUrlsForAudit } from './referencePages';
import { buildReferencePageExportEntry } from './referencePageExport';
import {
  REFERENCE_PAGE_CSV_HEADERS,
  referencePageEntryToCsvRow,
} from './referencePageReportFormat';

function escapeCell(val) {
  const s = String(val ?? '').replace(/\r\n/g, '\n');
  if (/[;"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv(headers, rows) {
  const lines = [
    headers.map(escapeCell).join(';'),
    ...rows.map((row) => row.map(escapeCell).join(';')),
  ];
  return `\uFEFF${lines.join('\n')}`;
}

function downloadCsv(filename, csvString) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeSlug(project) {
  return (project?.slug || project?.name || 'projet').replace(/[^a-z0-9-_]/gi, '-').slice(0, 40);
}

function dateStamp() {
  return new Date().toISOString().split('T')[0];
}

export { buildReferencePageFullNarrative } from './referencePageReportFormat';

/**
 * Charge pagespeed_raw manquant avant export.
 */
export async function prepareReportPagesForExport(slug, project, reportPages) {
  const pages = reportPages || [];
  return Promise.all(
    pages.map(async (page) => {
      let perf = page.perf;
      if (perf?.pagespeed_average > 0 && !perf?.pagespeed_raw) {
        const resolved = page.resolvedUrl
          || resolveReferenceUrlsForAudit([page.url], project?.url)[0]
          || page.url;
        try {
          const audit = await getAuditByUrl(slug, resolved);
          if (audit?.pagespeed_raw) {
            perf = { ...perf, ...audit, pagespeed_raw: audit.pagespeed_raw };
          }
        } catch {
          /* audit absent */
        }
      }
      return { ...page, perf };
    }),
  );
}

function buildEntries(project, reportPages, todosByNorm) {
  return (reportPages || [])
    .filter((p) => p?.url)
    .map((page) => {
      const norm = normalizePageUrl(page.resolvedUrl || page.url);
      const altNorm = normalizePageUrl(page.url);
      const todo = todosByNorm[norm] || todosByNorm[altNorm] || todosByNorm[page.url] || null;
      return buildReferencePageExportEntry(
        {
          url: page.url,
          title: page.title,
          meta_description: page.meta_description,
        },
        page.seo,
        page.perf,
        page.errors,
        todo,
      );
    });
}

/**
 * Télécharge un CSV avec une ligne par page de référence (contenu complet).
 */
export async function downloadReferencePagesCsv(slug, project, reportPages, todosByNorm = {}) {
  const pages = (reportPages || []).filter((p) => p?.url);
  if (!pages.length) {
    throw new Error('Aucune page de référence à exporter.');
  }

  const prepared = await prepareReportPagesForExport(slug, project, pages);
  const entries = buildEntries(project, prepared, todosByNorm);
  const projectName = project?.name || project?.slug || '';
  const rows = entries.map((entry) => referencePageEntryToCsvRow(entry, projectName));

  const csv = buildCsv(REFERENCE_PAGE_CSV_HEADERS, rows);
  const filename = `pages-reference-${safeSlug(project)}-${dateStamp()}.csv`;
  downloadCsv(filename, csv);
}
